import { useLayoutEffect, useRef, useState, CSSProperties } from 'react';
import { useCheckoutState } from './checkoutState';
import useIsMobileDevice from './useIsMobileDevice';
import useScroll from './useScroll';

type ScrollingRules = { content: CSSProperties; basket: CSSProperties };

const screenHeight = window.innerHeight;
let oldContentHeight: number | undefined;
let oldBasketHeight: number | undefined;
let externalHeader: number | undefined;

const getTop = (elementHeight: number) => {
  const isShorterThanScreenHeight = elementHeight + externalHeader! < screenHeight;
  return isShorterThanScreenHeight ? 0 + externalHeader! : (elementHeight - screenHeight) * -1;
};

const useStickyScrolling = () => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollingRules, setScrollingRules] = useState<ScrollingRules>({ content: {}, basket: {} });
  const { currentStep, checkoutState } = useCheckoutState();
  const isMobile = useIsMobileDevice();

  const updateScrollingRules = () => {
    if (isMobile || !HAS_BASKET) {
      return;
    }

    const contentHeight = contentRef.current?.scrollHeight;
    const basketHeight = contentRef.current?.nextElementSibling?.children[0]?.scrollHeight;
    const somethingChanged = contentHeight !== oldContentHeight || basketHeight !== oldBasketHeight;

    oldContentHeight = contentHeight;
    oldBasketHeight = basketHeight;

    if (contentHeight && basketHeight && somethingChanged) {
      if (!externalHeader) {
        externalHeader = contentRef.current?.offsetTop;
      }

      const newHeight = { content: contentHeight, basket: basketHeight };
      const newRules = { content: {}, basket: {} };
      const shorterElement = contentHeight >= basketHeight ? 'basket' : 'content';

      newRules[shorterElement] = { top: getTop(newHeight[shorterElement]) };
      setScrollingRules(newRules);
    }
  };

  useScroll(() => updateScrollingRules(), { isHeightWatcher: true });
  useLayoutEffect(() => updateScrollingRules(), [currentStep, checkoutState.orderState.basket]);

  return { contentRef, contentStyles: scrollingRules.content, basketStyles: scrollingRules.basket };
};

export default useStickyScrolling;
